import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, Phone, Calendar, DollarSign, CheckCircle2, Receipt, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicInvoice() {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const fetchInvoice = () => fetch(`${API}/api/public/invoices/${invoiceId}`)
    .then(r => r.ok ? r.json() : Promise.reject(r))
    .then(d => { setInvoice(d); setLoading(false); return d; });

  useEffect(() => {
    fetchInvoice()
      .then(() => {
        fetch(`${API}/api/public/invoices/${invoiceId}/mark-viewed`, { method: 'POST' }).catch(() => {});
      })
      .catch(async (r) => {
        const e = r.json ? await r.json().catch(() => ({})) : {};
        setError(e.detail || 'Invoice not found');
        setLoading(false);
      });
  }, [invoiceId]);

  // Auto-verify Stripe redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId || !searchParams.get('paid')) return;
    setVerifying(true);
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await fetch(`${API}/api/public/invoices/${invoiceId}/checkout-status/${sessionId}`);
        const d = await r.json();
        if (d.payment_status === 'paid' || d.status === 'complete') {
          await fetchInvoice();
          if (d.just_marked_paid) toast.success('Payment received — thank you!');
          setVerifying(false);
          return;
        }
        if (d.status === 'expired') { toast.error('Checkout session expired'); setVerifying(false); return; }
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setVerifying(false);
          toast.message('Payment is still processing. Refresh in a few seconds.');
        }
      } catch (_) {
        setVerifying(false);
      }
    };
    poll();
  }, [invoiceId, searchParams]);

  const startCheckout = async () => {
    setPaying(true);
    try {
      const r = await fetch(`${API}/api/public/invoices/${invoiceId}/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not start checkout');
      window.location.href = d.checkout_url;
    } catch (e) {
      toast.error(e.message);
      setPaying(false);
    }
  };

  if (loading) return <CenterPanel><Loader2 className="h-7 w-7 animate-spin text-red-500" /></CenterPanel>;
  if (error) return (
    <CenterPanel>
      <AlertCircle className="h-12 w-12 text-red-500" />
      <h1 className="text-2xl font-bold mt-3">Invoice Unavailable</h1>
      <p className="text-gray-500 mt-1">{error}</p>
      <p className="text-xs text-gray-400 mt-4">Call (678) 846-3009 if you believe this is an error.</p>
    </CenterPanel>
  );

  const fmt$ = (n) => `$${Number(n || 0).toLocaleString()}`;
  const isPaid = invoice.status === 'paid';
  const balance = invoice.balance_due || 0;
  const subtotal = invoice.subtotal || 0;
  const discount = invoice.discount || 0;
  const deposit = invoice.deposit_paid || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-black text-white px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-black tracking-tight text-lg" data-testid="ace-home-link">ACE <span className="text-red-500">Auto Concierge</span></Link>
          <a href="tel:+16788463009" className="flex items-center gap-2 text-sm text-gray-300 hover:text-white"><Phone className="h-4 w-4" /> (678) 846-3009</a>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="public-invoice-page">
        <p className="text-[10px] uppercase tracking-widest text-red-600 font-bold flex items-center gap-1.5"><Receipt className="h-3 w-3" /> Invoice</p>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight mt-1" data-testid="invoice-title">{invoice.bill_to || 'Invoice'}</h1>
        <p className="text-xs text-gray-500 mt-1">Invoice <span className="font-mono">{invoice.invoice_id}</span></p>

        {isPaid && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-testid="invoice-paid-banner">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <p className="font-bold text-green-900">Paid in full</p>
              <p className="text-xs text-green-700">Thank you. A copy of this invoice has been retained for your records.</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 mt-5 overflow-hidden">
          <div className="p-5 border-b">
            <h2 className="font-bold text-lg">Service Details</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 text-sm">
            <Detail icon={Calendar} label="Event date" value={invoice.event_date} />
            <Detail label="Vehicle" value={invoice.vehicle} />
            <Detail label="Service" value={invoice.service_description} full />
          </div>

          <div className="p-5 bg-gray-50 border-t space-y-2 text-sm">
            <Row label="Subtotal" value={fmt$(subtotal)} />
            {discount > 0 && <Row label="Discount" value={`- ${fmt$(discount)}`} />}
            {deposit > 0 && <Row label="Deposit paid" value={`- ${fmt$(deposit)}`} />}
            <div className="pt-2 border-t border-gray-200 flex items-center justify-between text-base font-black">
              <span>{isPaid ? 'Total' : 'Balance due'}</span>
              <span className={isPaid ? 'text-green-600' : 'text-red-600'} data-testid="invoice-balance-due">{fmt$(isPaid ? subtotal - discount : balance)}</span>
            </div>
          </div>
        </div>

        {!isPaid && balance > 0 && (
          <div className="mt-5 bg-white rounded-xl border border-gray-200 p-5" data-testid="payment-block">
            <h2 className="font-bold text-lg flex items-center gap-2"><DollarSign className="h-5 w-5 text-red-600" /> Pay Balance</h2>
            <p className="text-sm text-gray-600 mt-1">Secure card payment processed by Stripe. Apple Pay & Google Pay supported.</p>
            {verifying && (
              <div className="mt-3 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="verifying-payment">
                <Loader2 className="h-4 w-4 animate-spin" /> Verifying payment…
              </div>
            )}
            <button
              onClick={startCheckout}
              disabled={paying || verifying}
              className="block mt-4 w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold h-12 rounded-lg flex items-center justify-center gap-2"
              data-testid="invoice-pay-now-btn"
            >
              {paying ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting to Stripe…</> : <><CreditCard className="h-5 w-5" /> Pay {fmt$(balance)} now</>}
            </button>
            <a href="tel:+16788463009" className="block mt-2 w-full text-center text-xs text-gray-500 hover:text-gray-700" data-testid="invoice-call-btn">
              Or call (678) 846-3009 to pay by phone
            </a>
          </div>
        )}

        <p className="text-[11px] text-gray-400 mt-6 text-center">
          ACE Auto Concierge · Atlanta, GA · (678) 846-3009 · YourAutoConcierge.com
        </p>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-gray-700">
    <span>{label}</span>
    <span className="font-mono">{value}</span>
  </div>
);

const Detail = ({ icon: Icon, label, value, full }) => {
  if (!value) return null;
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </p>
      <p className="mt-1 text-sm font-medium text-gray-800">{value}</p>
    </div>
  );
};

const CenterPanel = ({ children }) => (
  <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">{children}</div>
);
